import type { CofQuizQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './cof-spelling-answer.module.css'

export interface CofSpellingAnswerProps {
  question: Extract<CofQuizQuestion, { kind: 'spell-enharmonic' }>
  interactive: boolean
  onAnswer: (picked: { kind: 'key'; name: string; ring: 'major' | 'minor' }) => void
}

/** Six enharmonic pairs (sharp label, flat label). Minor variants append 'm'. */
const MAJOR_PAIRS: readonly (readonly [string, string])[] = [
  ['F#', 'Gb'],
  ['C#', 'Db'],
  ['B', 'Cb'],
  ['Ab', 'G#'],
  ['Eb', 'D#'],
  ['Bb', 'A#'],
]

const MINOR_PAIRS: readonly (readonly [string, string])[] = MAJOR_PAIRS.map(([s, f]) => [`${s}m`, `${f}m`])

/**
 * CoF Wheel v1.5 enharmonic-spelling answer surface — 12 buttons in 6 pairs.
 * The clicked label dispatches `{ kind: 'key', name, ring }`; the `m` suffix is
 * stripped from the dispatched name (ring carries the mode), matching the
 * validator's `picked.name + 'm'` reconstruction.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.3
 */
export const CofSpellingAnswer: FC<CofSpellingAnswerProps> = props => {
  const { question, interactive, onAnswer } = props
  const ring = question.referenceKey.ring
  const pairs = ring === 'major' ? MAJOR_PAIRS : MINOR_PAIRS
  return (
    <div className={styles.grid} role="group" aria-label="Enharmonic spelling choices">
      {pairs.map(([a, b]) => (
        <div key={`${a}-${b}`} className={styles.pair}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            className={styles.btn}
            disabled={!interactive}
            onClick={() => onAnswer({ kind: 'key', name: a.replace(/m$/, ''), ring })}
          >
            {a}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            className={styles.btn}
            disabled={!interactive}
            onClick={() => onAnswer({ kind: 'key', name: b.replace(/m$/, ''), ring })}
          >
            {b}
          </Button>
        </div>
      ))}
    </div>
  )
}
